import React, { useState } from 'react'
import { useFormContext, Controller } from 'react-hook-form'
import { Info } from 'lucide-react'

import { OrderCoreSchemaType } from '../orderCore.schema'
import { FormGrid } from '../styles/shared'
import { SelectFormGroup, InfoLink, InfoLinkText } from './MainInfos.style'
import InfoTooltip from '../../../../components/InfoTooltip'
import CompanySelect from '../../FashionOrders/SelectItem/CompanySelect'
import VendorSelect from '../../FashionOrders/SelectItem/VendorSelect'

export interface SelectSource {
  apiUrl: string
  queryParams?: Record<string, string | number>
  allowCreate?: boolean
}

interface MainInfosProps {
  supplier: SelectSource
  seller: SelectSource
  client: SelectSource
  children?: React.ReactNode
}

const buildAddressString = (entity: any): string => {
  const safe = (v: any): string => {
    if (!v && v !== 0) return ''
    if (typeof v === 'string') return v
    if (typeof v === 'number') return String(v)
    if (typeof v === 'object' && v !== null) return v.name || v.label || ''
    return ''
  }
  const street = safe(entity.street)
  const number = safe(entity.number)
  const neighborhood = safe(entity.neighborhood)
  const city = safe(entity.city)
  const state = safe(entity.state)
  const rawCep = entity.cep || ''
  const cepDigits = String(rawCep).replace(/\D/g, '')
  const cepPadded = cepDigits ? cepDigits.padStart(8, '0') : ''
  const cepFormatted = cepPadded ? `${cepPadded.slice(0, 5)}-${cepPadded.slice(5)}` : ''
  const parts: string[] = []
  if (street) parts.push(`${street}${number ? `, ${number}` : ''}`)
  if (neighborhood) parts.push(neighborhood)
  if (city) parts.push(city)
  if (state) parts.push(state)
  const main = parts.join(' - ')
  const result = cepFormatted ? `${main}${main ? ' - ' : ''}${cepFormatted}` : main
  return result.trim()
}

const getCompanyDetailsForTooltip = (company: any): Record<string, string> => {
  const details: Record<string, string> = {}
  if (company) {
    details['Razão Social'] = String(company.companyName || company.name || 'Não informado')
    if (company.isMain) {
      const tipo = company.tipoEstabelecimento || (company.cnpj ? (company.cnpj.replace(/\D/g, '').substring(8, 12) === '0001' ? 'Matriz' : 'Filial') : '')
      if (tipo) details['Tipo'] = tipo
    }
    details['CNPJ/CPF'] = String(company.cnpj || company.cpf || 'Não informado')
    details['Nome do Contato'] = String(company.contactName || company.contato || (company.contacts?.[0]?.name) || 'Não informado')
    details['Email'] = String(company.email || (company.contacts?.[0]?.email) || 'Não informado')
    details['Telefone'] = String(company.phone || company.telefone || (company.contacts?.[0]?.phoneNumber) || 'Não informado')
    const address = buildAddressString(company)
    if (address) details['Endereço'] = address
  }
  return details
}

const getSellerDetailsForTooltip = (seller: any): Record<string, string> => {
  const details: Record<string, string> = {}
  if (seller) {
    details['Nome'] = String(seller.name || 'Não informado')
    details['Email'] = String(seller.email || 'Não informado')
  }
  return details
}

const MainInfos: React.FC<MainInfosProps> = ({ supplier, seller, client, children }) => {
  const { control, watch, setValue } = useFormContext<OrderCoreSchemaType>()

  const [activeInfo, setActiveInfo] = useState<string | null>(null)

  const selectedSupplierId = watch('supplierId')
  const selectedClientId = watch('clientId')
  const selectedSellerId = watch('sellerId')
  const selectedSupplier = watch('supplierDetails')
  const selectedClient = watch('clientDetails')
  const selectedSeller = watch('sellerDetails')

  const handleInfoToggle = (type: string, hasDetails: boolean) => {
    if (!hasDetails) return
    setActiveInfo(activeInfo === type ? null : type)
  }

  return (
    <>
    <FormGrid>
      <SelectFormGroup>
        <Controller
          name="supplierId"
          control={control}
          render={({ field }) => (
            <CompanySelect
              label="Meu CNPJ"
              required={true}
              apiUrl={supplier.apiUrl}
              displayField="companyName"
              queryParams={supplier.queryParams}
              value={selectedSupplier || { id: Number(field.value || 0), companyName: watch('supplier') || '' }}
              onChange={(v: any) => {
                field.onChange(v.id || undefined)
                setValue('supplier', v.companyName || '')
                setValue('supplierDetails', v)
                setActiveInfo(null)
              }}
            />
          )}
        />
        <InfoLink
          disabled={!selectedSupplierId}
          onClick={() => handleInfoToggle('supplier', !!selectedSupplierId)}
        >
          <Info size={12} />
          <InfoLinkText>Mais informações</InfoLinkText>
        </InfoLink>
        {activeInfo === 'supplier' && selectedSupplier && (
          <InfoTooltip details={getCompanyDetailsForTooltip(selectedSupplier)} />
        )}
      </SelectFormGroup>

      <SelectFormGroup>
        <Controller
          name="sellerId"
          control={control}
          render={({ field }) => (
            <VendorSelect
              label="Vendedor"
              required={true}
              apiUrl={seller.apiUrl}
              displayField="name"
              value={selectedSeller || { id: field.value || 0, name: watch('seller') || '' }}
              onChange={(v: any) => {
                field.onChange(v.id || undefined)
                setValue('seller', v.name || '')
                setValue('sellerDetails', v)
                setActiveInfo(null)
              }}
            />
          )}
        />
        <InfoLink
          disabled={!selectedSellerId}
          onClick={() => handleInfoToggle('seller', !!selectedSellerId)}
        >
          <Info size={12} />
          <InfoLinkText>Mais informações</InfoLinkText>
        </InfoLink>
        {activeInfo === 'seller' && selectedSeller && (
          <InfoTooltip details={getSellerDetailsForTooltip(selectedSeller)} />
        )}
      </SelectFormGroup>

      <SelectFormGroup>
        <Controller
          name="clientId"
          control={control}
          render={({ field }) => (
            <CompanySelect
              label="Cliente"
              required={true}
              apiUrl={client.apiUrl}
              displayField="companyName"
              queryParams={client.queryParams}
              allowCreate={client.allowCreate}
              value={selectedClient || { id: Number(field.value || 0), companyName: watch('client') || '' }}
              onChange={(v: any) => {
                field.onChange(v.id || undefined)
                setValue('client', v.companyName || '')
                setValue('clientDetails', v)
                setActiveInfo(null)
              }}
            />
          )}
        />
        <InfoLink
          disabled={!selectedClientId}
          onClick={() => handleInfoToggle('client', !!selectedClientId)}
        >
          <Info size={12} />
          <InfoLinkText>Mais informações</InfoLinkText>
        </InfoLink>
        {activeInfo === 'client' && selectedClient && (
          <InfoTooltip details={getCompanyDetailsForTooltip(selectedClient)} />
        )}
      </SelectFormGroup>
    </FormGrid>

    {children}
    </>
  )
}

export default MainInfos
